import { useCallback, useEffect, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useCreateTool } from '@/services/queries/tools'

type CreateToolModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialToolName?: string
  onToolCreated?: (tool: { id: string; name: string }) => Promise<void> | void
  ariaLabel?: string
  description?: string
}

export const CreateToolModal = ({
  open,
  onOpenChange,
  initialToolName = '',
  onToolCreated,
  ariaLabel = 'Create tool dialog',
  description = 'Create a new tool.',
}: CreateToolModalProps) => {
  const [draftName, setDraftName] = useState('')
  const { mutateAsync: createTool, isPending: isCreatingTool } = useCreateTool()
  const normalizedDraftName = useMemo(() => draftName.trim(), [draftName])

  useEffect(() => {
    /**
     * Invariant: reset the draft from caller-provided value each time the modal
     * is opened so users always see the latest typed context.
     */
    if (open) {
      setDraftName(initialToolName.trim())
    }
  }, [open, initialToolName])

  const handleCreateTool = useCallback(async () => {
    if (!normalizedDraftName || isCreatingTool) {
      return
    }

    try {
      const createdTool = await createTool({ name: normalizedDraftName })

      if (typeof createdTool?.id !== 'string' || createdTool.id.trim() === '') {
        return
      }

      const createdToolName =
        typeof createdTool?.name === 'string' && createdTool.name.trim() !== ''
          ? createdTool.name
          : normalizedDraftName

      await onToolCreated?.({
        id: createdTool.id,
        name: createdToolName,
      })

      onOpenChange(false)
    } catch (error) {
      // useCreateTool owns toast/error behavior.
      console.error(error)
    }
  }, [
    createTool,
    isCreatingTool,
    normalizedDraftName,
    onOpenChange,
    onToolCreated,
  ])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ariaLabel={ariaLabel}
        className="max-w-lg min-h-0"
        data-testid="create-tool-modal"
      >
        <DialogHeader>
          <DialogTitle>Create Tool</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Input
          placeholder="Tool name"
          value={draftName}
          autoFocus
          data-testid="create-tool-modal-name-input"
          onChange={(event) => setDraftName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              void handleCreateTool()
            }
          }}
        />
        <DialogFooter className="mt-2">
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            data-testid="create-tool-modal-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleCreateTool()
            }}
            disabled={!normalizedDraftName || isCreatingTool}
            data-testid="create-tool-modal-submit"
          >
            {isCreatingTool ? 'Creating...' : 'Create Tool'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
